import {TRACKING_QUERY_PARAMS} from './constants';
import {SignupRequestResponse, useSignupRequestMutation} from './mutations';
import {
  useCountryQuery,
  usePhonePrefixesQuery,
  usePortalLanguagesQuery,
} from './queries';
import {errorMessages} from './translations';
import {
  CombinedCountryData,
  CreatePortalResponse,
  PortalLanguage,
  SignupForm,
  Step,
} from './types';
import {useNormalizedQueryParams} from './utils';
import Intercom from '@intercom/messenger-js-sdk';
import {HubConnection} from '@microsoft/signalr';
import {useGlobalization} from '@planday-corp/globalization';
import {useService, useTelemetry} from '@planday-corp/react-app';
import {Option} from '@planday-corp/ui/types';
import {orderBy} from 'lodash';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {AppContext} from '~/App';
import {
  capitalizeFirstLetter,
  socketConnection,
} from '~/shared/helpers/helpers';

export const useCombinedCountryData = () => {
  const {l10n} = useGlobalization();

  const {data: countries, isLoading: isLoadingCountries} = useCountryQuery(
    l10n.locale
  );

  const {data: phonePrefixes, isLoading: isLoadingPhonePrefixes} =
    usePhonePrefixesQuery();

  const isLoading = isLoadingPhonePrefixes || isLoadingCountries;

  const mappedData = React.useMemo<CombinedCountryData[]>(() => {
    if (phonePrefixes && countries) {
      return phonePrefixes.map(phonePrefix => {
        const country = countries.find(
          country => country.code === phonePrefix.shortName
        );

        return {
          prefixNumber: phonePrefix.phonePrefix,
          countryCode: phonePrefix.shortName,
          countryName: country?.name,
        };
      });
    }
    return [];
  }, [phonePrefixes, countries]);

  return {isLoading, data: mappedData};
};

interface AzureAppInsightsTrackingOpts {
  keyPrefix?: string;
}

export const useFieldValueChangeMonitoring = () => {
  const [value, setValue] = useState<number | string | undefined>();

  const monitorValue = (currentValue: number | string | undefined) => {
    setValue(currentValue);
  };

  const isValueDirty = (currentValue: number | string | undefined) => {
    return value !== currentValue;
  };

  return {value, monitorValue, isValueDirty};
};

export const useAzureAppInsightsTracking = (
  opts?: AzureAppInsightsTrackingOpts
) => {
  const telemetry = useTelemetry();

  const normalizedQueryParams = useNormalizedQueryParams(TRACKING_QUERY_PARAMS);

  const track = (
    name: string,
    properties?: Record<string, string | number | boolean>
  ) => {
    telemetry.track({
      name: opts?.keyPrefix ? `${opts.keyPrefix}.${name}` : name,
      properties: {
        source: 'PlandaySignup',
        ...properties,
        ...normalizedQueryParams,
      },
    });
  };

  return {
    track,
  };
};

export const useSignUpTrackingEvents = () => {
  const {track} = useAzureAppInsightsTracking({keyPrefix: 'PortalSignup'});

  return {
    firstName: () => track('FilledOutFirstName'),
    lastName: () => track('FilledOutLastName'),
    email: () => track('FilledOutEmail'),
    countryCode: (code: string) => track('SelectedCountryCode', {code}),
    portalLanguage: (language: string) =>
      track('SelectedPortalLanguageId', {language}),
    phoneNumber: () => track('FilledOutPhoneNumber'),
    password: () => track('FilledOutPassword'),
    acceptTerms: () => track('AcceptedTerms'),
    businessName: () => track('FilledOutBusinessName'),
    country: (country: string) => track('FilledOutCountry', {country}),
    industry: (industry: string) => track('FilledOutIndustry', {industry}),
    employeeSize: (size: string) => track('FilledOutEmployeeSize', {size}),
    next: (step: Step) => track('ClickedNextButton', {step}),
    previous: (step: Step) => track('ClickedPreviousButton', {step}),
    createButton: () => track('ClickedCreateButton'),
    portalReady: (portalId: string) => track('PortalReady', {portalId}),
  };
};

export const useCreatePortal = (
  form: SignupForm,
  context: AppContext
): {
  status: 'pending' | 'success' | 'error';
  signupRequestResponse?: SignupRequestResponse['outputModel'];
  error?: string;
  createPortalResponse: CreatePortalResponse | null;
} => {
  const {mutate, error: mutationError, data} = useSignupRequestMutation();
  const {i18n} = useGlobalization();
  const requestId = data?.outputModel?.requestId;
  const connection = useRef<HubConnection | null>(null);
  const [portalStatus, setPortalStatus] = useState<
    'pending' | 'success' | 'error'
  >('pending');
  const [wsError, setWsError] = useState<string | null>(null);
  const [createPortalResponse, setCreatePortalResponse] = useState(null);

  useEffect(() => {
    if (mutationError) {
      setPortalStatus('error');
    }
  });
  const createWebSocketConnection = useCallback(async () => {
    if (!requestId) return;

    try {
      const newConnection = socketConnection(context);
      if (!newConnection) {
        throw new Error('Failed to initialize WebSocket connection');
      }

      newConnection.on('onPending', () => setPortalStatus('pending'));
      newConnection.on('onSuccess', response => {
        setPortalStatus('success');
        setCreatePortalResponse(response);
      });
      newConnection.on('onFailure', (errorMsg: string) => {
        setPortalStatus('error');
        setWsError(errorMsg);
      });

      await newConnection.start();
      await newConnection.invoke('Subscribe', requestId);
      connection.current = newConnection;
    } catch (err) {
      console.error('WebSocket connection failed:', err);
      setWsError(`${i18n.t(errorMessages.generic)}`);
    }
  }, [requestId]);

  useEffect(() => {
    mutate(form);
  }, []);

  useEffect(() => {
    if (!connection.current && requestId) {
      createWebSocketConnection();
    }
    return () => {
      connection.current?.stop();
    };
  }, [createWebSocketConnection, requestId]);

  return {
    status: portalStatus,
    signupRequestResponse: data?.outputModel,
    error: wsError || data?.failureMessage || mutationError?.statusText,
    createPortalResponse,
  };
};

export const getDeviceCategory = () => {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes('mobi')) {
    return 'mobile';
  } else if (
    ua.includes('tablet') ||
    (ua.includes('ipad') && !ua.includes('mobi'))
  ) {
    return 'tablet'; // Includes tablets
  } else {
    return 'desktop'; // Default to desktop
  }
};

export const useIntercom = () => {
  const {config} = useService<AppContext>();

  useEffect(() => {
    Intercom({
      app_id: config.get('intercomAppId'),
      // Hardcoded to EU region since our only region is in the EU
      region: 'eu',
    });
  }, [config]);
};

export const useWindowDimensions = () => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({width: window.innerWidth, height: window.innerHeight});
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return dimensions;
};

export const usePortalLanguagesData = (): {
  portalLanguageOptions: Option<number>[];
  portalLanguages: PortalLanguage[];
} => {
  const {l10n} = useGlobalization();
  const {data} = usePortalLanguagesQuery(l10n.locale);

  return {
    portalLanguageOptions:
      orderBy(
        data?.map(language => ({
          label: capitalizeFirstLetter(language.name),
          value: language.id,
        })),
        ['label'],
        ['asc']
      ) || [],
    portalLanguages: data || [],
  };
};
